var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
/* eslint-disable jsx-a11y/no-interactive-element-to-noninteractive-role */
import { useEffect, useState, useRef, useImperativeHandle, forwardRef, } from "react";
import { useMedia } from "../utils";
import { ChartJS, } from "../types/types";
import { Wrapper } from "./ChartComponent.styles";
export const gridColor = (background) => background === "white" ? "#CCCCCC" : "rgba(255,255,255,.5)";
export const defaultTooltipProps = {
    position: "average",
    mode: "index",
    xAlign: "center",
    yAlign: "bottom",
    intersect: false,
    backgroundColor: "rgba(0,0,0, 1)",
    cornerRadius: 0,
};
export const chartDefaults = (background, title, { legendPosition = "bottom", titlePosition = "top", } = {}) => ({
    legend: {
        display: true,
        labels: {
            color: background !== "white" ? "rgba(255,255,255,0.8)" : "#707070",
            boxWidth: 12,
            boxHeight: 12,
            font: {
                family: "'AktivGrotesk',helvetica,arial,sans-serif",
                size: 13,
            },
            padding: 20,
        },
        position: legendPosition,
        align: "start",
        onHover: (e) => {
            e.native.target.style.cursor = "pointer";
        },
        onLeave: (e) => {
            e.native.target.style.cursor = "default";
        },
    },
    title: {
        display: true,
        color: background !== "white" ? "rgba(255,255,255,1)" : "#000",
        text: title,
        position: titlePosition,
        align: "start",
        padding: {
            bottom: 25,
        },
        font: {
            weight: "bold",
            family: "'AktivGrotesk',helvetica,arial,sans-serif",
            size: 16,
        },
    },
});
export const ChartComponent = forwardRef((props, ref) => {
    const { className, height, width, redraw = false, type, data, options = {}, plugins = [], getDatasetAtEvent, getElementAtEvent, getElementsAtEvent, fallbackContent, responsive, aspectRatio, } = props;
    const canvas = useRef(null);
    const chartHeight = responsive && typeof responsive === "object"
        ? // eslint-disable-next-line react-hooks/rules-of-hooks
            useMedia(["(min-width: 1024px)", "(min-width: 768px)", "(min-width: 320px)"], [
                responsive.large.height,
                responsive.medium.height,
                responsive.small.height,
            ], height || 300)
        : height || 300;
    const chartWidth = responsive && typeof responsive === "object"
        ? // eslint-disable-next-line react-hooks/rules-of-hooks
            useMedia(["(min-width: 1024px)", "(min-width: 768px)", "(min-width: 320px)"], [
                responsive.large.width,
                responsive.medium.width,
                responsive.small.width,
            ], +width || 300)
        : width || 300;
    const [chart, setChart] = useState();
    useImperativeHandle(ref, () => chart, [chart]);
    const renderChart = () => {
        if (!canvas.current)
            return;
        if (aspectRatio) {
            options.aspectRatio = aspectRatio || 16 / 9;
        }
        setChart(new ChartJS(canvas.current, {
            type,
            data: data,
            // @ts-ignore
            options: Object.assign(Object.assign({}, options), { maintainAspectRatio: !!aspectRatio, responsive: true, resizeDelay: 10 }),
            // @ts-ignore
            plugins: [...plugins],
        }));
    };
    const onClick = (e) => {
        if (!chart)
            return;
        if (getDatasetAtEvent) {
            getDatasetAtEvent(chart.getElementsAtEventForMode(e, "dataset", { intersect: true }, false), e);
        }
        if (getElementAtEvent) {
            getElementAtEvent(chart.getElementsAtEventForMode(e, "nearest", { intersect: true }, false), e);
        }
        if (getElementsAtEvent) {
            getElementsAtEvent(chart.getElementsAtEventForMode(e, "index", { intersect: true }, false), e);
        }
    };
    const updateChart = () => {
        if (!chart)
            return;
        if (options) {
            chart.options = Object.assign(Object.assign({}, options), { events: ["mousemove", "mouseout", "click", "touchstart", "touchmove"], plugins: Object.assign({}, options.plugins) });
        }
        const _a = data, { datasets: newDataSets = [] } = _a, newChartData = __rest(_a, ["datasets"]);
        const { datasets: currentDataSets = [] } = chart.config.data;
        // copy values
        Object.assign(chart.config.data, newChartData);
        // @ts-ignore
        chart.config.data.datasets = newDataSets.map((newDataSet) => {
            // given the new set, find it's current match
            const currentDataSet = currentDataSets.find((d) => d.label === newDataSet.label && d.type === newDataSet.type);
            // There is no original to update, so simply add new one
            if (!currentDataSet || !newDataSet.data)
                return newDataSet;
            currentDataSet.data.length = newDataSet.data.length;
            // copy in values
            Object.assign(currentDataSet.data, newDataSet.data);
            // apply dataset changes, but keep copied data
            return Object.assign(Object.assign(Object.assign({}, currentDataSet), newDataSet), { data: currentDataSet.data });
        });
        chart.update();
    };
    const destroyChart = () => {
        if (chart)
            chart.destroy();
    };
    function handlePropsUpdate() {
        if (redraw) {
            destroyChart();
            setTimeout(() => {
                renderChart();
            }, 0);
        }
        else {
            updateChart();
        }
    }
    useEffect(() => {
        setTimeout(() => {
            renderChart();
        }, 0);
        return () => destroyChart();
    }, []);
    useEffect(() => {
        handlePropsUpdate();
    }, [canvas.current, data, chartHeight, chartWidth]);
    const isPieOrDonut = type === "doughnut" || type === "pie";
    return (_jsx(Wrapper, Object.assign({ chartHeight: chartHeight, chartWidth: chartWidth, isPieOrDonut: isPieOrDonut }, { children: _jsx("canvas", Object.assign({ ref: canvas, className: className, onClick: onClick, "data-testid": "chart-canvas", role: "img" }, { children: fallbackContent })) })));
});
ChartComponent.displayName = "ChartComponent";
